import React from 'react';
// import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SectionTitle from '../components/SectionTitle';

// Define interface types for our data
interface TeamMember {
  name: string;
  role: string;
  bio: string;
  image?: string;
  longBio?: string;
}

interface ValueItem {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

interface Milestone {
  year: string;
  event: string;
  description: string;
}

const AboutPage: React.FC = () => {
  // const { t } = useTranslation();

  // Default data, to be used if translation fails or is removed
  const defaultTeamMembers: TeamMember[] = [
    {
      name: 'Denis Kreuzer',
      role: 'Founder & CEO',
      bio: 'Passionate about empowering small businesses through technology.',
      image: '/images/team/denis_kreuzer.jpg',
      longBio: 'Denis has over 10 years of experience in software development and has seen firsthand how digital platforms can both help and hinder small enterprises. He started Fork:it to provide a fairer alternative.'
    },
    {
      name: 'Max Mustermann',
      role: 'Lead Developer',
      bio: 'Building the future of independent digital solutions.',
      image: '/images/team/max_mustermann.jpg',
      longBio: 'Max is a full-stack developer with a knack for creating user-friendly and robust applications. He believes in the power of open source and community-driven development.'
    },
    {
      name: 'Erika Musterfrau',
      role: 'UX/UI Designer',
      bio: 'Designing intuitive and beautiful experiences for our users.',
      image: '/images/team/erika_musterfrau.jpg',
      longBio: 'Erika focuses on creating seamless user journeys and visually appealing interfaces. Her goal is to make complex technology accessible and enjoyable for everyone.'
    }
  ];

  const defaultValues: ValueItem[] = [
    {
      title: 'Independence',
      description: 'We believe in digital sovereignty and freedom from platform monopolies.',
      icon: (
        <svg className="w-10 h-10 text-primary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-3.517 3.272-5 5-5s5 1.483 5 5c0 1.804-1.02 3.402-2.515 4.25C20.04 16.014 20 16.706 20 17.5c0 .578-.072 1.133-.206 1.663M4 11c0-3.517 3.272-5 5-5s5 1.483 5 5c0 1.804-1.02 3.402-2.515 4.25C10.04 16.014 10 16.706 10 17.5c0 .578-.072 1.133-.206 1.663M15 12H9m6 3H9m6 3H9M9 9V6a3 3 0 013-3h0a3 3 0 013 3v3" />
        </svg>
      )
    },
    {
      title: 'Fairness',
      description: 'Transparent pricing and no hidden fees. We want to be a partner at eye level.',
      icon: (
        <svg className="w-10 h-10 text-primary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 0V5m0 14v-3m-7-1a7 7 0 1114 0M5 11a7 7 0 1114 0" />
        </svg>
      )
    },
    {
      title: 'Community',
      description: 'Together we are stronger. We promote exchange and collaboration.',
      icon: (
        <svg className="w-10 h-10 text-primary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-2.207M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg> // Placeholder icon, consider updating
      )
    }
  ];

  const defaultMilestones: Milestone[] = [
    { year: '2022', event: 'Idea & Concept', description: 'The first idea for Fork:it was born out of frustration with existing platform dependencies.' },
    { year: '2023', event: 'Prototyping & Feedback', description: 'Development of initial prototypes and gathering feedback from small businesses.' },
    { year: '2024', event: 'Launch of First Solutions', description: 'Rollout of the first set of tools aimed at digital independence.' },
    { year: 'Future', event: 'Expanding the Ecosystem', description: 'Continuously developing new solutions and fostering a strong community.' }
  ];

  // Use safe translation with defaults
  const getTeamMembers = (): TeamMember[] => {
    // try {
    //   const translated = t('about.teamMembers', { returnObjects: true });
    //   // Check if it's actually an array with the expected properties
    //   if (Array.isArray(translated) && translated.length > 0 && 'name' in translated[0]) {
    //     return translated as TeamMember[];
    //   }
    //   return defaultTeamMembers;
    // } catch (error) {
    //   return defaultTeamMembers;
    // }
    return defaultTeamMembers; // Directly return default data
  };

  const getValues = (): ValueItem[] => {
    // try {
    //   const translated = t('about.values', { returnObjects: true });
    //   // Check if it's actually an array with the expected properties
    //   if (Array.isArray(translated) && translated.length > 0 && 'title' in translated[0]) {
    //     return translated as ValueItem[];
    //   }
    //   return defaultValues;
    // } catch (error) {
    //   return defaultValues;
    // }
    return defaultValues; // Directly return default data
  };
  
  const getMilestones = (): Milestone[] => {
    // try {
    //   const translated = t('about.milestones', { returnObjects: true });
    //   // Check if it's actually an array with the expected properties
    //   if (Array.isArray(translated) && translated.length > 0 && 'year' in translated[0]) {
    //     return translated as Milestone[];
    //   }
    //   return defaultMilestones;
    // } catch (error) {
    //   return defaultMilestones;
    // }
    return defaultMilestones; // Directly return default data
  };

  const teamMembers = getTeamMembers();
  const values = getValues();
  const milestones = getMilestones();

  return (
    <main className="pb-16">
      {/* Hero Section with Background Image */}
      <section 
        className="relative h-screen flex items-start justify-center pt-32"
        style={{
          backgroundImage: "url('/images/team/team-group.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
          <h1 className="font-lexend font-bold text-5xl text-white mb-4">
            Fork:it
          </h1>
          <h2 className="font-lexend font-semibold text-3xl text-white mb-6">
            About Us
          </h2>
          <p className="text-white text-xl max-w-3xl mx-auto">
            We are a collective of subversive free thinkers using technology to build a more inclusive digital world.
            In an aging society where digital advancements often leave elderly populations behind, 
            we're committed to restoring balance and creating tools that serve real human needs.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-lexend font-bold text-3xl text-slate-900 dark:text-white mb-6">
                Our Mission: Digital Independence For All
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                Great possibilities in digital development have always led to some people being left behind. Our focus is on elderly people - a major group in our aging society who need support to maintain their digital self-determination but are often overlooked by mainstream tech.
              </p>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                We believe that everyone deserves access to digital tools that enhance their lives, even if they aren't contributing to the endless stream of content on social media platforms. As critical minds, we question the status quo of digital engagement and create alternatives that truly serve people rather than exploit them.
              </p>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                By harnessing AI and technology for good, we're giving back to society and creating systems that respect users' autonomy, privacy, and unique needs regardless of their technical proficiency. Our subversive approach challenges the dominant narratives about who technology should serve and how it should be designed.
              </p>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                In an aging society, we see tremendous untapped potential in bridging the digital divide - not by forcing elderly people to adapt to poorly designed systems, but by creating intuitive interfaces that adapt to their needs and respect their existing knowledge and skills.
              </p>
            </div>
            <div className="relative">
              <div className="bg-primary/10 rounded-sm p-8 h-80 flex items-center justify-center">
                <div className="font-lexend font-bold text-7xl text-primary/80 text-center">
                  {/* {t('about.tagline')} */}
                  We are forking the internet.
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-slate-800 rounded-sm p-6 shadow-lg">
                <div className="font-lexend font-semibold text-lg text-slate-900 dark:text-white">
                  {/* {t('about.movementSince')} */}
                  Movement since <span className="text-primary dark:text-primary-300">2022</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Founder */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Meet Our Founder"
            subtitle="The visionary behind Fork:it's mission for digital self-determination"
            centered
          />
          
          <div className="max-w-5xl mx-auto mt-12 bg-white dark:bg-slate-800 rounded-sm overflow-hidden shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-3">
              <div className="col-span-1">
                <div 
                  className="h-full min-h-[300px] bg-primary/10 dark:bg-primary-900/30 flex items-center justify-center"
                  style={{
                    backgroundImage: "url('/images/team/denis.jpg')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {/* Fallback for missing image */}
                  <span className="font-lexend font-bold text-8xl text-primary/50 dark:text-primary-300/50 opacity-0">
                    DK
                  </span>
                </div>
              </div>
              <div className="col-span-2 p-8">
                <h3 className="font-lexend font-bold text-2xl text-slate-900 dark:text-white mb-2">
                  Denis Kreuzer
                </h3>
                <p className="text-primary dark:text-primary-300 font-medium mb-6">
                  Founder & CEO
                </p>
                <p className="text-slate-700 dark:text-slate-300 mb-4 font-medium">
                  Multidisciplinary Full-Stack Developer & Product Strategist with over 15 years of experience.
                </p>
                <p className="text-slate-700 dark:text-slate-300 mb-4">
                  Denis brings a unique blend of technical depth and creative vision to Fork:it. With extensive experience spanning full-stack development, AI integration, design systems, and product strategy, he bridges the crucial gap between UX, branding, and engineering.
                </p>
                <p className="text-slate-700 dark:text-slate-300 mb-4">
                  As a critical mind and subversive free thinker, Denis is passionate about using AI to do good for people and giving back to society. He believes in the power of technology to create more inclusive digital ecosystems, with a special focus on elderly people—a major group often left behind in the digital revolution.
                </p>
                <blockquote className="border-l-4 border-primary dark:border-primary-400 pl-4 italic text-slate-700 dark:text-slate-300 mb-4">
                  "In an aging society, we need to support those who might not be uploading videos to social media, but who have just as much right to digital self-determination. The great possibilities in digital development have always led to some being left behind—our mission is to change that."
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Our Values"
            subtitle="The principles that guide our actions"
            centered
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {values.map((value: ValueItem, index: number) => (
              <div key={index} className="bg-white dark:bg-slate-800 p-6 rounded-sm shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all">
                <div className="mb-4 bg-primary/5 dark:bg-primary-900/30 w-12 h-12 rounded-full flex items-center justify-center">
                  {value.icon}
                </div>
                <h3 className="font-lexend font-semibold text-xl mb-2 text-slate-900 dark:text-white">
                  {value.title}
                </h3>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Our Team"
            subtitle="The critical minds working together to create more inclusive digital solutions"
            centered
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {teamMembers.filter(member => member.name !== "Denis Kreuzer").map((member: TeamMember, index: number) => (
              <div key={index} className="bg-white dark:bg-slate-800 rounded-sm overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all">
                <div 
                  className="h-48 flex items-center justify-center bg-primary/10 dark:bg-primary-800/20"
                  style={{
                    backgroundImage: `url('${member.image || ''}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Initials shown if image fails to load */}
                  <span className={`font-lexend font-bold text-4xl text-primary/50 dark:text-primary-300/50 ${member.image ? 'opacity-0' : 'opacity-100'}`}>
                    {member.name.split(' ')[0][0]}{member.name.split(' ')[1][0]}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-lexend font-bold text-xl text-slate-900 dark:text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-primary dark:text-primary-300 font-medium text-sm mb-4">
                    {member.role}
                  </p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm mb-4">
                    {member.bio}
                  </p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                    {member.longBio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Milestones */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Milestones"
            subtitle="The journey of Fork:it"
            centered
          />
          
          <div className="max-w-3xl mx-auto mt-16">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 h-full w-0.5 bg-primary/20"></div>
              
              {/* Timeline items */}
              <div className="space-y-12">
                {milestones.map((milestone: Milestone, index: number) => (
                  <div key={index} className={`relative flex flex-col md:flex-row ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                    {/* Dot */}
                    <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-8 h-8 bg-white dark:bg-slate-800 rounded-full border-2 border-primary dark:border-primary-400 flex items-center justify-center">
                      <div className="w-3 h-3 bg-primary dark:bg-primary-400 rounded-full"></div>
                    </div>
                    
                    {/* Content */}
                    <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                      <span className="inline-block px-3 py-1 bg-primary/10 dark:bg-primary-900/30 text-primary dark:text-primary-300 rounded-sm text-sm font-medium mb-2">
                        {milestone.year}
                      </span>
                      <h3 className="font-lexend font-bold text-xl text-slate-900 dark:text-white mb-2">
                        {milestone.event}
                      </h3>
                      <p className="text-slate-700 dark:text-slate-300 text-sm">
                        {milestone.description}
                      </p>
                    </div>
                    
                    {/* Empty space for opposite side */}
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-lexend font-bold text-3xl text-slate-900 dark:text-white mb-6">
              {/* {t('about.joinTitle')} */}
              Join Our Mission
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-8">
              {/* {t('about.joinText')} */}
              Become part of a community that is reshaping the digital landscape for small businesses.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-primary hover:bg-primary-600 text-white px-6 py-3 rounded-sm font-lexend font-medium text-base shadow-sm transition-all"
              >
                {/* {t('about.contactUs')} */}
                Contact Us
              </Link>
              <Link
                to="/partner-werden"
                className="bg-transparent border border-primary text-primary dark:text-primary-300 dark:border-primary-500 px-6 py-3 rounded-sm font-lexend font-medium text-base hover:bg-primary/5 dark:hover:bg-primary-900/30 transition-all"
              >
                {/* {t('about.becomePartner')} */}
                Become a Partner
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage; 